import { Tooltip, UtilityButton } from '@lego/connect-components-react';
import type React from 'react';
import { useState } from 'react';
import type { Accept } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { normalizeRow } from '../../utils/print';

export type FileDropzoneProps = {
  onFilesAccepted?: (files: File[]) => void;
  onFilesParsed?: (parsedData: Record<string, string | number>[]) => void;
  onParseError?: (error: unknown) => void;
  accept?: Accept;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
};

const TooltipContent: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="text-core-slate-1100">
      <div className="mb-1 flex flex-col justify-center p-2.5 w-full">
        <div className=" flex flex-col bg-white">
          <strong className="font-bold">{t('newPrintPage.fileDropzone.tooltipTitle')}</strong>
          <span className="mb-1">
            {t('newPrintPage.fileDropzone.tooltipAccepted')}{' '}
            <span className="font-semibold">.xlsx, .csv</span>
          </span>
          <span className="mb-1">{t('newPrintPage.fileDropzone.tooltipFirstRow')}</span>
          <span className="mb-1">{t('newPrintPage.fileDropzone.tooltipColumnData')}</span>
          <span className="mb-1">{t('newPrintPage.fileDropzone.tooltipExample')}</span>
        </div>
        <table className="bg-core-slate-50 rounded text-body-sm table-auto mt-2">
          <thead>
            <tr>
              <th className="px-2 py-1 min-w-[80px]">quantity</th>
              <th className="px-2 py-1 min-w-[100px]">var_1</th>
              <th className="px-2 py-1 min-w-[100px]">pick_list</th>
              <th className="px-2 py-1 min-w-[80px]">pick</th>
              <th className="px-2 py-1 min-w-[120px]">date</th>
              <th className="px-2 py-1 min-w-[120px]">date2</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>5</td>
              <td>SampleText</td>
              <td>123</td>
              <td>vvvv</td>
              <td>13/02/2024</td>
              <td>14/02/2024</td>
            </tr>
            <tr>
              <td>3</td>
              <td>AnotherText</td>
              <td>678.9</td>
              <td>OptionB</td>
              <td>01/03/2024</td>
              <td>02/03/2024</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFilesAccepted,
  onFilesParsed,
  onParseError,
  accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/csv': ['.csv'],
  } as Accept,
  multiple = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);

  const parseFiles = async (fileList: File[]) => {
    const allData: Record<string, string | number>[] = [];
    try {
      for (const file of fileList) {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });
        const normalizedJson = (json as Record<string, string>[]).map(normalizeRow);
        allData.push(...normalizedJson);
      }

      onFilesParsed?.(allData);
    } catch (error) {
      console.error('Failed to parse files:', error);
      onParseError?.(error);
    }
  };

  const handleDrop = (newFiles: File[]) => {
    const uniqueFiles = newFiles.filter((file) => !files.some((f) => f.name === file.name));
    const updatedFiles = [...files, ...uniqueFiles];
    setFiles(updatedFiles);
    onFilesAccepted?.(updatedFiles);
    parseFiles(updatedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple,
    disabled,
    onDrop: handleDrop,
  });

  return (
    <div
      {...(getRootProps() as React.InputHTMLAttributes<HTMLInputElement>)}
      className={`mt-[20px] border-2 border-dashed rounded-200 p-400 text-center cursor-pointer transition-colors duration-200 ${
        isDragActive ? 'border-core-blue-900 bg-core-blue-50' : 'border-core-slate-100 bg-white'
      } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <input {...(getInputProps() as React.InputHTMLAttributes<HTMLInputElement>)} />
      <div className="text-body-sm text-core-slate-1100 inline-flex">
        {isDragActive ? (
          t('newPrintPage.fileDropzone.dropHere')
        ) : (
          <div className="relative ml-2">
            {t('newPrintPage.fileDropzone.dragAndDrop')}
            <Tooltip position="top" variant="neutral" maxWidth={640}>
              <Tooltip.Trigger>
                <UtilityButton
                  iconName="circle-question"
                  size="small"
                  appearance="ghost"
                  aria-label={t('newPrintPage.fileDropzone.tooltipTitle')}
                  onClick={(event) => event.stopPropagation()}
                />
              </Tooltip.Trigger>
              <Tooltip.Content>
                <TooltipContent />
              </Tooltip.Content>
            </Tooltip>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <ul className="mt-200 text-left text-body-xs text-core-slate-1300">
          {files.map((file) => (
            <li key={file.name} className="flex items-center justify-between font-bold">
              <span>{file.name}</span>
              <UtilityButton
                iconName="trash"
                appearance="ghost"
                size="small"
                aria-label={`${t('newPrintPage.fileDropzone.removeFile')}: ${file.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedFiles = files.filter((f) => f.name !== file.name);
                  setFiles(updatedFiles);
                  onFilesAccepted?.(updatedFiles);
                  parseFiles(updatedFiles);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileDropzone;
